// Shared bar primitive — the single definition of how a bar looks and behaves
// across the app. Extracted from DealPipeline, which was the reference
// treatment: an inset padded track, a pill fill, a two-stop gradient of the
// bar's own colour, and an `anim-grow-*` transition gated by <Reveal>.
//
// Loading is a first-class state rather than a separate skeleton component.
// The same element stays mounted at zero length with `is-loading`, so when the
// data arrives the bar grows into place instead of a placeholder being swapped
// for a finished chart. Callers that know their labels ahead of the data should
// render real rows with `loading` set rather than substituting a ghost.

/** px stub kept under a loading bar so its colour is on screen from the start. */
export const BAR_STUB = 8;

/** Track height for a standard horizontal bar row. */
export const BAR_TRACK = "h-7";

/**
 * Two-stop gradient in a bar's own colour — translucent at the origin,
 * full strength at the tip. `to right` for horizontal, `to top` for vertical.
 */
export function barFill(color: string, direction: Direction) {
  const to = direction === "vertical" ? "to top" : "to right";
  return `linear-gradient(${to}, color-mix(in srgb, ${color} 55%, transparent), ${color})`;
}

/**
 * Positional accent → secondary blend. Drives funnel-style charts where a row's
 * depth in the sequence, not its value, should set the tone.
 */
export function barBlend(index: number, count: number) {
  const last = Math.max(count - 1, 1);
  const share = Math.round(100 - (index / last) * 100);
  return `color-mix(in srgb, var(--accent) ${share}%, var(--secondary))`;
}

export type Direction = "horizontal" | "vertical";

/**
 * The fill element itself, without a track. Use when the caller already owns
 * the track markup (a plot area with gridlines, a custom-height row).
 */
export function BarFill({
  fraction,
  color = "var(--accent)",
  direction = "horizontal",
  loading = false,
  gradient = true,
  className = "",
}: {
  /** 0–1 of the track. Values outside the range are clamped. */
  fraction: number;
  color?: string;
  direction?: Direction;
  loading?: boolean;
  /** false paints a flat colour instead of the two-stop gradient. */
  gradient?: boolean;
  className?: string;
}) {
  const vertical = direction === "vertical";
  // Loading bars sit at zero and grow when the real value lands.
  const size = loading ? 0 : Math.min(Math.max(fraction, 0), 1) * 100;
  const stub = loading ? BAR_STUB : 6;
  const background = gradient ? barFill(color, direction) : color;

  return (
    <div
      className={`${vertical ? "anim-grow-y w-full" : "anim-grow-x h-full"} rounded-full ${
        loading ? "is-loading" : ""
      } ${className}`}
      style={{
        ...(vertical
          ? { height: `${size}%`, minHeight: stub }
          : { width: `${size}%`, minWidth: stub }),
        background,
      }}
    />
  );
}

/**
 * A bar in its own inset track — the complete DealPipeline treatment. The `p-1`
 * inset gives the fill a visible gutter so a full bar still reads as sitting
 * inside a track rather than overflowing it.
 */
export default function Bar({
  fraction,
  color = "var(--accent)",
  direction = "horizontal",
  loading = false,
  gradient = true,
  thickness,
  className = "",
}: {
  fraction: number;
  color?: string;
  direction?: Direction;
  loading?: boolean;
  gradient?: boolean;
  /** Track thickness class. Defaults to `h-7` horizontal / `w-full` vertical. */
  thickness?: string;
  className?: string;
}) {
  const vertical = direction === "vertical";
  const track = thickness ?? (vertical ? "w-full h-full" : BAR_TRACK);

  return (
    <div
      className={`neu-inset rounded-full p-1 ${track} ${
        vertical ? "flex flex-col justify-end" : ""
      } ${className}`}
    >
      <BarFill
        fraction={fraction}
        color={color}
        direction={direction}
        loading={loading}
        gradient={gradient}
      />
    </div>
  );
}
